import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../core/data/redux/store';
import { createEmployee, fetchEmployeeAnalytics, getEmployeeById, getEmployees, updateEmployee } from '../core/data/redux/employee/employeeAction';
import { message } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAppConfig } from './useAppConfig';
import { ex } from '@fullcalendar/core/internal-common';
import { getUserById } from '../core/data/redux/user/userAction';
import { setCreatedEmployeeId } from '../core/data/redux/employee/employeeSlice';
dayjs.extend(utc);

interface EmployeeFormValues {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: Date | null;
  gender: number;
  username: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  employeeRole: number;
  profilePicture: File | null;
  about: string;
  employmentType: string;
  workPreference: string;
  joiningDate: Date | null;
  reportingManager: number;
  hr: number | string;
  hod: number | string;
  experiencelabel?: string;
  pfEnable: boolean;
  locationTracking?: boolean;
  department: number;
  designation: number;
  company: string;
  employee_status: string;
  branch?: string;
  job_location?: string;
  checked?: boolean;
  // hr: number
}

export const useBasicInfo = (setTableKey: React.Dispatch<React.SetStateAction<number>>, handleCancel: any, showFullDetailsTab: any, modalType: any, setShowFullDetailsTab: React.Dispatch<React.SetStateAction<boolean>>) => {
  const { employeeFullDetails } = useSelector((state: any) => state.employee);
  const dispatch = useDispatch<AppDispatch>();
  const employeeId = employeeFullDetails?.employee[0]?.EmployeeID
  const { activeCompany } = useSelector((state: any) => state.companyDetails);
  const createdEmployeeId = useSelector((state: RootState) => state.employee.createdEmployeeId);
  const { user } = useSelector((state: any) => state.auth);
  const config = useAppConfig();
  const [employeeData, setEmployeeData] = useState<EmployeeFormValues>({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: null,
    gender: 0,
    username: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    employeeRole: 0,
    profilePicture: null,
    about: '',
    employmentType: '',
    workPreference: '',
    joiningDate: null,
    reportingManager: 0,
    hr: 0,
    hod: 0,
    experiencelabel: '',
    pfEnable: false,
    locationTracking: false,
    department: 0,
    designation: 0,
    company: activeCompany?.company_name || '',
    employee_status: '',
    branch: '',
    job_location: '',
    checked: false,
  });

  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [employeeErrors, setEmployeeErrors] = useState<{ [key: string]: string }>({});


  useEffect(() => {
    if (employeeFullDetails && modalType === "edit" && employeeFullDetails.employee.length > 0) {
      const employee = employeeFullDetails.employee[0];
      setEmployeeData({
        firstName: employee?.FirstName || '',
        lastName: employee?.LastName || '',
        email: employee?.Email || '',
        dateOfBirth: employee?.DateOfBirth ? dayjs(employee.DateOfBirth).toDate() : new Date(),
        gender: employee?.GenderId || 0,
        username: employee?.Username || '',
        password: '',
        confirmPassword: '',
        phoneNumber: employee?.Phone || '',
        employeeRole: employee?.role_id || 0,
        profilePicture: null,
        about: employee?.Description || '',
        employmentType: employee?.EmploymentType || '',
        workPreference: employee?.WorkPreference || '',
        joiningDate: employee?.DateOfJoining ? dayjs(employee.DateOfJoining).toDate() : new Date(),
        experiencelabel: employee?.experience || '',
        reportingManager: employee?.SupervisorId || 0,
        pfEnable: employee?.pfEnable ?? false,
        locationTracking: employee?.locationTracking || '',
        department: employee?.DepartmentId || 0,
        designation: employee?.DesignationId || 0,
        company: employee?.Company || activeCompany?.company_name || '',
        employee_status: employee?.employee_status || '',
        branch: employee?.branch || '',
        job_location: employee?.job_location || '',
        checked: employee?.check || false,
        hr: employee?.hrId || 0,
        hod: employee?.hod_id || 0
      });
      if (employee?.PictureURL) {
        setProfileImagePreview(employee?.PictureURL);
      }
    }
  }, [employeeFullDetails]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setEmployeeData(prevData => ({ ...prevData, [name]: checked }));
    } else if (e.target instanceof HTMLInputElement && e.target.files) {
      // ✅ Handle file upload
      if (name === 'profilePicture') {
        const file = e.target.files[0];
        setEmployeeData(prevData => ({ ...prevData, [name]: file }));

        const reader = new FileReader();
        reader.onloadend = () => {
          setProfileImagePreview(reader.result as string);
        };
        if (file) {
          reader.readAsDataURL(file);
        }
      }
    } else if (name === 'gender' || name === 'department' || name === 'designation' || name === 'reportingManager' || name === 'employeeRole') {
      // ✅ Parse integers
      setEmployeeData(prevData => ({ ...prevData, [name]: parseInt(value, 10) }));
    } else {
      // ✅ Handle all other values
      setEmployeeData(prevData => ({ ...prevData, [name]: value }));
    }
  };


  const resetForm = (mode: 'all' | 'full' | 'additional' | 'employement' | 'jobInfo' | 'location_info' = 'full') => {
    if (mode === 'all') {
      setEmployeeData({
        firstName: '',
        lastName: '',
        email: '',
        dateOfBirth: null,
        gender: 0,
        username: '',
        password: '',
        confirmPassword: '',
        phoneNumber: '',
        employeeRole: 0,
        profilePicture: null,
        about: '',
        employmentType: '',
        workPreference: '',
        joiningDate: null,
        reportingManager: 0,
        hr: 0,
        hod: 0,
        experiencelabel: '',
        pfEnable: false,
        locationTracking: false,
        department: 0,
        designation: 0,
        company: activeCompany?.company_name || '',
        employee_status: '',
        branch: '',
        job_location: '',
        checked: false,
      });
      setProfileImagePreview(null);
      setEmployeeErrors({});
      return;
    }
  };

  const handleEmployeeSubmit = async (e?: React.FormEvent, mode: 'full' | 'additional' | 'employement' | 'jobInfo' | 'location_info' = 'full', closeAfterSave: boolean = true, clearForm: boolean = false, showMessage: boolean = true): Promise<boolean> => {
    if (e) e?.preventDefault();
    if (employeeData.about.length > 999) message.warning("Description should be less than 999 characters");

    if (employeeData.dateOfBirth && employeeData.joiningDate) {
      if (dayjs(employeeData.joiningDate).isBefore(dayjs(employeeData.dateOfBirth))) {
        message.warning("Joining Date cannot be before Date of Birth");
        return false;
      }
    }

    const validationResult = validate(mode);
    if (!validationResult.isValid) {
      if (showMessage && validationResult.missingFields && validationResult.missingFields.length > 0) {
        const modeMessage = mode === "full" ? "Please fill all required fields to add/update Basic Info"
          : mode === "additional" ? "Please fill all required fields in Additional Information"
            : mode === "employement" ? "Please fill all required fields in Employment Details"
              : mode === "jobInfo" ? "Please fill all required fields in Job Information"
                : mode === "location_info" ? "Please fill all required fields in Location Information"
                  : "Please fill all required fields";
        message.warning(modeMessage);
      }
      return false;
    }

    let payload: any;

    if (mode === 'full') {
      payload = {
        Name: `${employeeData.firstName} ${employeeData.lastName}`,
        Email: employeeData.email,
        FirstName: employeeData.firstName,
        LastName: employeeData.lastName,
        UserId: 0,
        password: employeeData.password,
        DateOfBirth: dayjs(employeeData.dateOfBirth).format('YYYY-MM-DD'),
        GenderId: employeeData.gender,
        mobile: employeeData.phoneNumber,
        ModifiedBy: 1,
        RoleId: employeeData.employeeRole,
        company_details_id: activeCompany?.company_detail_id,
        financial_year_id: activeCompany?.financial_year_id,
        profilePicture: employeeData.profilePicture,
        maxUser: user?.total_user
      };
    } else if (mode === 'additional') {
      payload = {
        Description: employeeData.about || " ",
        ModifiedBy: 1,
      };
    } else if (mode === 'employement') {
      payload = {
        employmentType: employeeData.employmentType,
        workPreference: employeeData.workPreference,
        SupervisorId: employeeData.reportingManager || 0,
        hrId: employeeData.hr || 0,
        hod_id: employeeData.hod || 0,
        DateOfJoining: dayjs(employeeData.joiningDate).format('YYYY-MM-DD'),
        experiencelabel: employeeData.experiencelabel || '',
        pfEnable: employeeData.pfEnable ? 1 : 0,
        locationTracking: employeeData.locationTracking || '',
        ModifiedBy: 1
      };
    } else if (mode === 'jobInfo') {
      payload = {
        DepartmentId: employeeData.department,
        DesignationId: employeeData.designation,
        ModifiedBy: 1,
        Company: employeeData.company || '',
        employee_status: employeeData.employee_status
      };
    } else if (mode === 'location_info') {
      payload = {
        branch: employeeData.branch || '',
        job_location: employeeData.job_location || '',
        checked: employeeData.checked || '',
      }
    }
    try {
      if ((employeeId || createdEmployeeId) && modalType === "edit" || (createdEmployeeId && mode !== 'full')) {
        const idToUpdate = employeeId || createdEmployeeId;
        const result = await dispatch(updateEmployee({ employeeId: idToUpdate, employeeData: payload }));
        console.log("EMPLOYEE UPDATE PAYLOAD", payload)//it gives the hrId array now show in the UI  backend send undefined hrId employee.modal 

        if (result.meta.requestStatus === "fulfilled") {
          if (showMessage) {
            message.success(
              mode === "full"
                ? "Employee updated successfully"
                : mode === "additional"
                  ? "Additional information saved successfully"
                  : mode === "employement"
                    ? "Employment details saved successfully"
                    : mode === "jobInfo"
                      ? "Job information saved successfully"
                      : mode === "location_info"
                        ? "Location information saved successfully"
                        : "Data saved successfully"
            );
          }
          if (clearForm) resetForm();
          await dispatch(getEmployees());
          await dispatch(getUserById(user?.UserId));
          dispatch(fetchEmployeeAnalytics());
          setTableKey((prevKey) => prevKey + 1);
          if (closeAfterSave && !showFullDetailsTab) {
            handleCancel();
          }
          return true;
        } else {
          if (showMessage) message.error("Something went wrong");
          return false;
        }
      } else {
        // Create employee
        const result = await dispatch(createEmployee(payload));
        if (result.meta.requestStatus === "fulfilled") {
          if (typeof result.payload !== "string") {
            const newId = result.payload?.data?.EmployeeId || result.payload?.data?.EmployeeID || result.payload?.data?.id
            if (newId) {
              dispatch(setCreatedEmployeeId(newId));
              await dispatch(getEmployeeById({ employeeId: newId }));
            }
          }
          if (mode === 'full') {
            setShowFullDetailsTab(true);
          }
          if (showMessage) {
            message.success("Employee created successfully");
          }
          if (clearForm) resetForm();
          await dispatch(getEmployees());
          await dispatch(getUserById(user?.UserId));
          dispatch(fetchEmployeeAnalytics());
          setTableKey((prevKey) => prevKey + 1);
          if (closeAfterSave && !showFullDetailsTab) {
            handleCancel();
          }
          return true;
        } else if (result.meta.requestStatus === "rejected") {
          if (typeof result.payload === 'string' && showMessage) {
            message.error(result.payload);
          }
          return false;
        } else {
          if (showMessage) message.error("Something went wrong");
          return false;
        }
      }
    } catch (error) {
      message.error("Failed to process the employee data.");
      return false;
    }
  };

  const isValidPhoneNumber = (number: any) => {
    // Must be 10 digits
    if (!/^\d{10}$/.test(number)) return false;//10
    if (/^(\d)\1{9}$/.test(number)) return false;//1111
    if (/^(\d{2})\1{4}$/.test(number)) return false;//2323
    if (/^[01]/.test(number)) return false;//!start 0, 1
    const invalidList = ["1234567890", "9876543210"];
    if (invalidList.includes(number)) return false;
    return true;
  };

  const validate = (mode: 'full' | 'additional' | 'employement' | 'jobInfo' | 'location_info'): { isValid: boolean; missingFields?: string[] } => {
    let tempErrors: { [key: string]: string } = {};
    let isValid = true;
    const missingFields: string[] = [];

    // === FULL MODE ===
    if (mode === 'full') {
      if (!employeeData.firstName) {
        tempErrors.firstName = "First name is required";
        missingFields.push("First name");
        isValid = false;
      } else if (!/^[A-Za-z\s]+$/.test(employeeData.firstName)) {
        tempErrors.firstName = "Only letters allowed";
        missingFields.push("First name");
        isValid = false;
      }

      if (!employeeData.lastName) {
        tempErrors.lastName = 'Last name is required';
        missingFields.push("Last name");
        isValid = false;
      } else if (!/^[A-Za-z\s]+$/.test(employeeData.lastName)) {
        tempErrors.lastName = 'Only letters allowed';
        missingFields.push("Last name");
        isValid = false;
      }
      if (!employeeData.email) {
        tempErrors.email = 'Email is required';
        missingFields.push("Email");
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(employeeData.email)) {
        tempErrors.email = 'Email is not valid';
        missingFields.push("Email");
        isValid = false;
      }
      if (modalType !== "edit") {
        if (!employeeData.password) {
          tempErrors.password = 'Password is required';
          missingFields.push("Password");
          isValid = false;
        } else if (employeeData.password.length < 6) {
          tempErrors.password = 'Password should be at least 6 characters';
          missingFields.push("Password");
          isValid = false;
        }

        if (!employeeData.confirmPassword) {
          tempErrors.confirmPassword = 'Confirm your password';
          missingFields.push("Confirm Password");
          isValid = false;
        } else if (employeeData.password !== employeeData.confirmPassword) {
          tempErrors.confirmPassword = 'Passwords do not match';
          missingFields.push("Confirm Password");
          isValid = false;
        }
      }
      if (!employeeData.gender) {
        tempErrors.gender = 'Gender is required';
        missingFields.push("Gender");
        isValid = false;
      }
      if (!employeeData.username) {
        tempErrors.username = 'Username is required';
        missingFields.push("Username");
        isValid = false;
      }
      if (!employeeData.dateOfBirth) {
        tempErrors.dateOfBirth = 'Date of Birth is required';
        missingFields.push("Date of Birth");
        isValid = false;
      }
      if (!employeeData.phoneNumber) {
        tempErrors.phoneNumber = 'Phone number is required';
        missingFields.push("Phone Number");
        isValid = false;
      } else if (!isValidPhoneNumber(employeeData.phoneNumber)) {
        tempErrors.phoneNumber = 'Please enter a valid and realistic 10-digit mobile number';
        missingFields.push("Phone Number");
        isValid = false;
      }
      if (!employeeData.employeeRole) {
        tempErrors.employeeRole = 'Role is required';
        missingFields.push("Role");
        isValid = false;
      }
    }

    //=== ADDITIONAL MODE ===
    if (mode === 'additional') {
      if (employeeData.about && employeeData.about.length > 999) {
        tempErrors.about = 'Max 999 characters';
        isValid = false;
      }
    }

    // === EMPLOYMENT MODE ===
    if (mode === 'employement') {
      if (!employeeData.employmentType) {
        tempErrors.employmentType = 'Employment Type is required';
        missingFields.push("Employment Type");
        isValid = false;
      }
      if (!employeeData.workPreference) {
        tempErrors.workPreference = 'Work Preference is required';
        missingFields.push("Work Preference");
        isValid = false;
      }
      if (!employeeData.joiningDate) {
        tempErrors.joiningDate = 'Joining Date is required';
        missingFields.push("Joining Date");
        isValid = false;
      }
      if (!employeeData.experiencelabel) {
        tempErrors.experiencelabel = 'Employment Level is required';
        missingFields.push("Employment Level");
        isValid = false;
      }
      if (employeeData.dateOfBirth && employeeData.joiningDate) {
        if (dayjs(employeeData.joiningDate).isBefore(dayjs(employeeData.dateOfBirth))) {
          tempErrors.joiningDate = 'Joining Date cannot be before Date of Birth';
          missingFields.push("Joining Date");
          isValid = false;
        }
      }
    }

    // === JOB INFO MODE ===
    if (mode === 'jobInfo') {
      if (!employeeData.department) {
        tempErrors.department = 'Department is required';
        missingFields.push("Department");
        isValid = false;
      }
      if (!employeeData.designation) {
        tempErrors.designation = 'Designation is required';
        missingFields.push("Designation");
        isValid = false;
      }
      if (!employeeData.employee_status) {
        tempErrors.employee_status = 'Employee Status is required';
        missingFields.push("Employee Status");
        isValid = false;
      }
      if (!employeeData.company) {
        tempErrors.company = 'Company is required';
        missingFields.push("Company");
        isValid = false;
      }
    }

    // === LOCATION INFO MODE ===
    if (mode === 'location_info') {
      if (!employeeData.branch) {
        tempErrors.branch = 'Branch is required';
        missingFields.push("Branch");
        isValid = false;
      }
      if (!employeeData.job_location) {
        tempErrors.job_location = 'Job Location is required';
        missingFields.push("Job Location");
        isValid = false;
      }
    }

    // === GPF Special Case ===
    if (!config?.pf_reporting_enabled && employeeData.pfEnable) {
      const dojYear = dayjs(employeeData.joiningDate).year();
      if (dojYear > 2004) {
        tempErrors.pfEnable = 'GPF cannot be enabled for joining date after 2004';
        isValid = false;
      }
    }

    setEmployeeErrors(tempErrors);
    return { isValid, missingFields };
  };

  return {
    employeeData,
    setEmployeeData,
    handleChange,
    handleEmployeeSubmit,
    employeeErrors,
    profileImagePreview,
    setProfileImagePreview,
    resetEmployeeForm: resetForm,
    validate,
    createdEmployeeId
  };
};
